<script setup lang="ts">
/* global google */
import type { MapMarker, Polyline } from "@/components/controls/map/maps";
import { onMounted, ref, shallowRef, watch } from "vue";
import { darkenHex } from "@/app/ui/colourUtils";
import { redPinColour } from "@/components/controls/map/maps";
import { isNotNullOrUndefined } from "@/app/apiutils";
import isEqual from "lodash-es/isEqual";
import { BrewManError } from "@/app/errors";
import messages from "@/plugins/i18n";
import environment from "@/environment";
import { MarkerClusterer } from "@googlemaps/markerclusterer";

export interface MapLoaderInterface {
  setBounds: () => void;
}

type GoogleMapMarker = google.maps.marker.AdvancedMarkerElement;
type GoogleMapMarkerWithBmMarker = GoogleMapMarker & { marker: MapMarker };
type GoogleMapPolyline = google.maps.Polyline;
type GoogleMap = google.maps.Map;
type InfoWindow = google.maps.InfoWindow;

interface BmMapLoaderProps {
  value?: MapMarker | null;
  markers?: MapMarker[];
  polylines?: Polyline[];
  updateBoundsOnMarkerUpdate?: boolean;
  visible?: boolean;
  useMarkerClusterer?: boolean;
}

interface BmMapLoaderEvents {
  (eventName: "input", googleMapMarker: GoogleMapMarkerWithBmMarker): void;
  (eventName: "place-click", placeId: string): void;
}

const props = withDefaults(defineProps<BmMapLoaderProps>(), {
  markers: () => [],
  polylines: () => [],
  updateBoundsOnMarkerUpdate: true,
  visible: true,
  useMarkerClusterer: false,
});

const emit = defineEmits<BmMapLoaderEvents>();

const googleMapRef = ref<HTMLDivElement | null>(null);
const mapLoadAttempts = ref(1);
const maxLoadAttempts = ref(3);
const googleMapMarkers = shallowRef<GoogleMapMarkerWithBmMarker[]>([]);
const googleMapPolylines = shallowRef<GoogleMapPolyline[]>([]);
const loading = ref(false);
let clusterer: MarkerClusterer | null = null;

function requireGoogleMapRef(
  mapRef: HTMLDivElement | null
): asserts mapRef is HTMLDivElement {
  if (mapRef === null) {
    throw new BrewManError(messages.google_map_cannot_find_map);
  }
}

function requireGoogleMap(
  map: GoogleMap | undefined
): asserts map is GoogleMap {
  if (map === undefined) {
    throw new BrewManError(messages.google_map_not_loaded);
  }
}

function requireInfoWindow(
  infoWindow: InfoWindow | undefined
): asserts infoWindow is InfoWindow {
  if (infoWindow === undefined) {
    throw new BrewManError(messages.google_map_info_window_not_created);
  }
}

let map: GoogleMap;
let infoWindow: InfoWindow;

const defaultZoomLevel = 10;

async function initMap() {
  // Load our required libs
  const [MapsLibrary, GeometryLibrary] = (await Promise.all([
    google.maps.importLibrary("maps"),
    google.maps.importLibrary("geometry"),
  ])) as [google.maps.MapsLibrary, google.maps.GeometryLibrary];

  const { Map, InfoWindow } = MapsLibrary;

  // We've tried too many times
  if (mapLoadAttempts.value > maxLoadAttempts.value) {
    loading.value = false;
    return;
  }

  // No ref, so lets wait
  if (!googleMapRef.value) {
    mapLoadAttempts.value += 1;
    setTimeout(initMap, 1000);
    return;
  }

  // We should now be able to expect the map
  requireGoogleMapRef(googleMapRef.value);

  // Create the map
  map = new Map(googleMapRef.value, {
    // Sets default values for maps. These will be overridden by setting markers
    zoom: defaultZoomLevel,
    center: { lat: 0, lng: 0 },
    mapTypeControl: false,
    streetViewControl: false,
    gestureHandling: "cooperative",
    mapId: environment.apiGmapsId,
  });

  infoWindow = new InfoWindow();

  google.maps.event.addListenerOnce(map, "idle", () => {
    loading.value = false;
  });

  map.addListener("click", (e: any) => {
    if (e.placeId) {
      emit("place-click", e.placeId);
    }
  });

  await updateMarkers();
  await updatePolylines();
  setBounds();

  if (props.value) {
    focusMarker(props.value);
  }
}

function buildPinTitle(bmMarker: MapMarker) {
  const labelValue =
    bmMarker.showSequence === false
      ? undefined
      : bmMarker.sequence?.toString() ?? "";

  return [bmMarker.pinLabelPrefix, labelValue]
    .filter(isNotNullOrUndefined)
    .join(" - ");
}

// Used for when a pin title exists, and manually creates scalable elements which can fit the entire text
function buildCustomPin(bmMarker: MapMarker) {
  const customPin = document.createElement("div");
  customPin.textContent = buildPinTitle(bmMarker);
  customPin.classList.add("google-map-pin");
  customPin.style.background = bmMarker.colour || redPinColour;

  const customPinPointer = document.createElement("div");
  customPinPointer.classList.add("google-map-pin-pointer");
  customPin.appendChild(customPinPointer);

  return customPin;
}

function markerClicked(googleMapMarker: GoogleMapMarkerWithBmMarker) {
  if (googleMapMarker.marker.meta?.placeId) {
    emit("place-click", googleMapMarker.marker.meta.placeId);
  } else {
    emit("input", googleMapMarker);
  }
}

function markerHovered(googleMarkerWithBmMarker: GoogleMapMarkerWithBmMarker) {
  requireInfoWindow(infoWindow);
  infoWindow.close();

  const content = document.createElement("div");
  content.textContent = googleMarkerWithBmMarker.marker.name;
  content.classList.add("info-window-container");

  if (
    googleMarkerWithBmMarker.marker.url &&
    googleMarkerWithBmMarker.marker.linkText
  ) {
    const link = document.createElement("a");
    link.href = googleMarkerWithBmMarker.marker.url;
    link.textContent = googleMarkerWithBmMarker.marker.linkText;

    content.appendChild(link);
  }

  if (content) {
    infoWindow.setContent(content);
    infoWindow.open(googleMarkerWithBmMarker.map, googleMarkerWithBmMarker);
  }
}

function markerHideInfoWindow(bmMarker: MapMarker) {
  requireInfoWindow(infoWindow);

  // If we're moving off of our currently selected marker, don't hide the window
  if (props.value && markersAreEqual(props.value, bmMarker)) {
    return;
  }

  infoWindow.close();
}

// Merges our marker into the google marker - enforces a type definition
function mergeGoogleMarkerWithMapMarker(
  googleMarker: GoogleMapMarker,
  bmMarker: MapMarker
): asserts googleMarker is GoogleMapMarkerWithBmMarker {
  // Force in our marker obj, this is quite iffy but extremely useful
  Object.assign(googleMarker, { marker: bmMarker });
}

/**
 * Builds an advanced marker from a given bmMarker. Does not add the marker
 * to the map.
 *
 * To add a marker to the map, use `marker.map = map`.
 */
function buildMarker(
  bmMarker: MapMarker,
  { AdvancedMarkerElement, PinElement }: google.maps.MarkerLibrary
) {
  if (bmMarker.lat === null || bmMarker.lng === null) {
    return;
  }

  const content =
    buildPinTitle(bmMarker) !== ""
      ? buildCustomPin(bmMarker)
      : new PinElement({
          // Default setup
          background: bmMarker.colour,
          borderColor: darkenHex(bmMarker.colour ?? redPinColour, 50),
          glyphColor: darkenHex(bmMarker.colour ?? redPinColour, 50),
        }).element;

  const googleMarker = new AdvancedMarkerElement({
    map: null,
    position: { lat: bmMarker.lat, lng: bmMarker.lng },
    content,
    zIndex: bmMarker.sequence ? bmMarker.sequence + props.markers.length : -1,
  });

  mergeGoogleMarkerWithMapMarker(googleMarker, bmMarker);

  googleMarker.element.addEventListener("click", () => {
    markerClicked(googleMarker);
  });

  googleMarker.element.addEventListener("mouseover", () => {
    markerHovered(googleMarker);
  });

  googleMarker.element.addEventListener("mouseout", () => {
    markerHideInfoWindow(bmMarker);
  });

  return googleMarker;
}

async function updateMarkers() {
  googleMapMarkers.value.forEach((marker) => (marker.map = null));
  googleMapMarkers.value = [];

  const MarkerLibrary = (await google.maps.importLibrary(
    "marker"
  )) as google.maps.MarkerLibrary;

  googleMapMarkers.value = props.markers
    .map((marker) => buildMarker(marker, MarkerLibrary))
    .filter(isNotNullOrUndefined);

  if (props.useMarkerClusterer) {
    if (!clusterer) {
      clusterer = new MarkerClusterer({
        markers: [],
        map,
      });
    }

    clusterer.clearMarkers();
    clusterer.addMarkers(googleMapMarkers.value);
  } else {
    googleMapMarkers.value.forEach((marker) => (marker.map = map));
  }
}

function createPolyLine(bmPolyline: Polyline) {
  requireGoogleMap(map);

  return new google.maps.Polyline({
    path: google.maps.geometry.encoding.decodePath(bmPolyline.encodedPolyline),
    strokeColor: bmPolyline.strokeColor,
    strokeOpacity: bmPolyline.strokeOpacity,
    strokeWeight: bmPolyline.strokeWeight,
    zIndex: bmPolyline.zIndex
      ? bmPolyline.zIndex + props.polylines?.length ?? 0
      : -1,
    map,
  });
}

async function updatePolylines() {
  googleMapPolylines.value.forEach((m) => m.setMap(null));

  googleMapPolylines.value = props.polylines.map((polyline) =>
    createPolyLine(polyline)
  );
}

function markersAreEqual(markerOne: MapMarker, markerTwo: MapMarker) {
  if (markerOne.key && markerTwo.key) {
    return markerOne.key === markerTwo.key;
  }

  return isEqual(markerOne, markerTwo);
}

async function focusMarker(bmMarker: MapMarker) {
  if (bmMarker.lat && bmMarker.lng) {
    requireGoogleMap(map);

    map.panTo({
      lat: bmMarker.lat,
      lng: bmMarker.lng,
    });
  }

  const googleMapMarker = googleMapMarkers.value.find(
    (googleMapMarkerWithBmMarker) => {
      markersAreEqual(googleMapMarkerWithBmMarker.marker, bmMarker);
    }
  );
  if (infoWindow && googleMapMarker) {
    markerHovered(googleMapMarker);
  }
}

function setBounds() {
  // Don't update if we have no markers to set our bounds to
  if (props.markers.length === 0) {
    return;
  }

  requireGoogleMap(map);

  // Don't update bounds if not required, and zoom level has changed from default
  if (!props.updateBoundsOnMarkerUpdate && map.getZoom() !== defaultZoomLevel) {
    return;
  }

  let bounds: google.maps.LatLngBounds = new google.maps.LatLngBounds();

  const firstPolylinePoint = googleMapPolylines.value.at(0)?.getPath().getAt(0);
  const linePoints = firstPolylinePoint ? [firstPolylinePoint] : [];
  const markerPoints = props.markers
    .filter((marker) => marker.includeInMapBounds && marker.lat && marker.lng)
    .map((marker) => new google.maps.LatLng(marker.lat!, marker.lng!));

  const fitPoints = linePoints.concat(markerPoints);

  for (const point of fitPoints) {
    bounds.extend(point);
  }

  // Ensure the map covers at least a range of lats and lngs.
  const lats = fitPoints.map((x) => x.lat());
  const lngs = fitPoints.map((x) => x.lng());
  if (fitPoints.length > 0) {
    const minLat = Math.min(...lats);
    const maxLat = Math.max(...lats);
    const minLng = Math.min(...lngs);
    const maxLng = Math.max(...lngs);

    const llPad = 0.002;
    bounds.extend(new google.maps.LatLng(maxLat - llPad, maxLng - llPad));
    bounds.extend(new google.maps.LatLng(minLat + llPad, minLng + llPad));
  }

  // Setting the map to fit around all of the points and adding padding of these given values
  map.fitBounds(bounds, {
    top: 30,
    right: 30,
    left: 30,
    bottom: 30,
  });
}

onMounted(async () => {
  await initMap();
});

watch(
  () => props.markers,
  async () => {
    await updateMarkers();
    setBounds();
  }
);

watch(
  () => props.visible,
  async () => {
    await updateMarkers();
    await updatePolylines();
    setBounds();
  }
);

watch(
  () => props.polylines,
  async () => {
    await updatePolylines();
    setBounds();
  }
);

watch(
  () => props.value,
  () => {
    if (props.value) {
      focusMarker(props.value);
    }
  }
);
</script>

<template>
  <div v-if="mapLoadAttempts > maxLoadAttempts">
    {{ $m.failed_to_load_map }}
  </div>
  <div v-else class="google-map-container">
    <v-progress-linear v-if="loading" indeterminate color="primary" />
    <div
      ref="googleMapRef"
      class="google-map"
      :style="{ visibility: visible ? 'visible' : 'hidden' }"
    ></div>
  </div>
</template>

<style lang="sass">
.google-map-container
  height: 100%

.google-map
  min-width: 100px
  height: 100%
  min-height: 100%

.google-map-pin
  border: 1px solid rgba(0, 0, 0, 0.33)
  border-radius: 9999px
  font-size: 14px
  padding: 4px 8px
  color: white

.google-map-pin-pointer
  z-index: -1
  position: absolute
  background: inherit
  filter: brightness(85%)
  width: 20px
  left: calc(50% - 10px)
  height: 15px
  clip-path: polygon(0 0, 100% 0, 50% 100%)

.info-window-container
  display: flex
  flex-flow: column
  align-items: center
  gap: 2px
  color: #505457
</style>
